/**
 * BLU-711 A2 — data access for the `/inventory/stocktakes` approval surface.
 *
 * One hook per concern so the page stays a layout file:
 *   - `useStocktakeTabCounts`   the Pending / Waiting chips (always loaded)
 *   - `useStocktakeProposalList` the visible tab
 *   - `useStocktakeProposalDetail` the selected proposal (id-keyed, so a deep
 *     link resolves even when the row is not in the current tab)
 *   - `useCounterpartyName`     whose count this is, from either side
 *   - `useStocktakeMutations`   decide / resolve / approve / reject / wait
 *
 * Only the CLIENT org can act on a proposal (the server enforces it in
 * `assertClientOrg`); the owner reads the same surface. `useStocktakeAuthority`
 * is the single place that answers "can this person act", so the list rows,
 * the detail footer and the triage step never disagree.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { toast } from '@/hooks/use-toast';
import { parseStocktakeDrift } from '@/lib/trpc-error-utils';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { useRole } from '@/hooks/use-role';
import type {
  StocktakeProposalSummary,
  StocktakeProposalTab,
} from '@threadheads/shared';

const LIST_STALE_MS = 15_000;

export function useStocktakeProposalList(
  tab: StocktakeProposalTab,
  locationId: string | undefined,
  enabled = true,
) {
  return trpc.stocktakes.list.useQuery(
    { tab, ...(locationId ? { locationId } : {}) },
    {
      enabled,
      staleTime: LIST_STALE_MS,
      placeholderData: (previous) => previous,
    },
  );
}

/** Pending + waiting are always loaded: they drive the segmented-tab counts. */
export function useStocktakeTabCounts(locationId: string | undefined) {
  const pending = useStocktakeProposalList('pending', locationId);
  const waiting = useStocktakeProposalList('waiting', locationId);
  return {
    pending: pending.data ?? [],
    waiting: waiting.data ?? [],
    isLoading: pending.isLoading || waiting.isLoading,
  };
}

/** How long after the apply landed we keep asking whether the Shopify push
 *  finished. The sweep persists its summary whatever the outcome, so a null
 *  older than this is a dead sweep, not a slow one — and polling forever on a
 *  row nobody will change is a background request that never stops. */
const PUSH_POLL_WINDOW_MS = 5 * 60_000;
const PUSH_POLL_MS = 5_000;

export function useStocktakeProposalDetail(proposalId: string | null) {
  return trpc.stocktakes.getDetail.useQuery(
    { proposalId: proposalId ?? '' },
    {
      enabled: !!proposalId,
      staleTime: LIST_STALE_MS,
      // The post-approve Shopify push runs fire-and-forget, so an applied count
      // arrives with `pushSummary: null` and fills in a few seconds later.
      // Poll only in that window, and only while it is recent.
      refetchInterval: (query) => {
        const detail = query.state.data;
        if (!detail || detail.status !== 'applied' || detail.pushSummary != null) return false;
        const appliedAt = detail.resolvedAt ? Date.parse(detail.resolvedAt) : Number.NaN;
        if (Number.isNaN(appliedAt)) return false;
        return Date.now() - appliedAt < PUSH_POLL_WINDOW_MS ? PUSH_POLL_MS : false;
      },
      refetchIntervalInBackground: false,
    },
  );
}

/**
 * The other side of the count, named. An owner sees the client (resolved from
 * the connected-clients roster); a client sees the 3PL that ran the count
 * (resolved from the shared location's owner org). Both fall back to a plain
 * noun rather than an id, so no sentence ever renders a uuid.
 */
export function useCounterpartyName() {
  const { t } = useTranslation('inventory');
  const { labelFor } = useClientOrgLabels();
  // `allLocations`, not `locations`: this RESOLVES A NAME (the shared
  // location's owner org) for a stored proposal, not a location to pick.
  const { allLocations } = useLocation();

  return useCallback(
    (proposal: Pick<
      StocktakeProposalSummary,
      'counterpartyOrganizationId' | 'ownerOrganizationId' | 'locationId'
    >): string => {
      const counterpartyIsOwner =
        proposal.counterpartyOrganizationId === proposal.ownerOrganizationId;
      if (counterpartyIsOwner) {
        const location = allLocations.find((l) => l.id === proposal.locationId);
        return (
          location?.owner_organization_name?.trim() ||
          t('stocktakes.org.providerFallback')
        );
      }
      return (
        labelFor(proposal.counterpartyOrganizationId) ??
        t('stocktakes.org.clientFallback')
      );
    },
    [labelFor, allLocations, t],
  );
}

export interface StocktakeAuthority {
  /** The caller's org owns the rows, so it is the org that approves. */
  isClientOrg: (proposal: Pick<StocktakeProposalSummary, 'clientOrganizationId'>) => boolean;
  /** Role tier matching the server's `requireRole([Admin, Coordinator])`. */
  hasDecisionRole: boolean;
}

export function useStocktakeAuthority(): StocktakeAuthority {
  const { organization } = useOrganization();
  const { isAdmin, isWarehouseCoordinator } = useRole();
  const callerOrgId = organization?.id;
  return {
    isClientOrg: (proposal) =>
      !!callerOrgId && proposal.clientOrganizationId === callerOrgId,
    hasDecisionRole: isAdmin() || isWarehouseCoordinator(),
  };
}

export function useStocktakeMutations() {
  const { t } = useTranslation('inventory');
  const utils = trpc.useUtils();

  const invalidate = useCallback(() => {
    void utils.stocktakes.list.invalidate();
    void utils.stocktakes.getDetail.invalidate();
  }, [utils]);

  const invalidateStock = useCallback(() => {
    invalidate();
    // An applied count rewrites sku_inventory, so the stock surfaces behind it
    // are stale the moment approve returns.
    void utils.inventory.getProducts.invalidate();
  }, [invalidate, utils]);

  const onError = useCallback(
    (title: string) => (error: { message: string }) => {
      toast({ title, description: error.message, variant: 'destructive' });
    },
    [],
  );

  const setLineDecisions = trpc.stocktakes.setLineDecisions.useMutation({
    onSuccess: invalidate,
    onError: onError(t('stocktakes.error.decision')),
  });
  const resolveFlag = trpc.stocktakes.resolveFlag.useMutation({
    onSuccess: invalidate,
    onError: onError(t('stocktakes.error.resolveFlag')),
  });
  const approve = trpc.stocktakes.approve.useMutation({
    onSuccess: invalidateStock,
    // A stock drift is not a failure, it is a question: the drift dialog names
    // the lines and offers "Approve anyway", so a destructive toast beside it
    // would be a second, louder answer to the same event.
    onError: (error) => {
      if (parseStocktakeDrift(error).isDrift) return;
      toast({
        title: t('stocktakes.error.approve'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  const retryPush = trpc.stocktakes.retryPush.useMutation({
    onSuccess: invalidate,
    onError: onError(t('stocktakes.error.retryPush')),
  });
  const reject = trpc.stocktakes.reject.useMutation({
    onSuccess: invalidate,
    onError: onError(t('stocktakes.error.reject')),
  });
  const setWaiting = trpc.stocktakes.setWaiting.useMutation({
    onSuccess: invalidate,
    onError: onError(t('stocktakes.error.waiting')),
  });

  return { setLineDecisions, resolveFlag, approve, reject, setWaiting, retryPush };
}
